import { fromZonedTime, toZonedTime } from 'date-fns-tz'

export interface Time {
  hours: number
  minutes: number
}

type RoundingStrategy = 'later' | 'sooner'

export interface CreationOptions {
  minutesRounding: RoundingStrategy
}

const DEFAULT_OPTIONS: CreationOptions = { minutesRounding: 'later' }

/**
 * Timezone the rental wall-clock is expressed in.
 *
 * A prebooking time means "10:00 at the pick-up station", not "10:00 wherever the
 * customer happens to be browsing from". Every city we operate in is CET/CEST with
 * EU-harmonised DST dates, so one zone covers the whole fleet. Swap for a per-city
 * `timeZone` in `CITIES` if we ever launch outside CET.
 */
export const RENTAL_TIME_ZONE = 'Europe/Berlin'

/**
 * Build a PrebookingDate from an instant.
 *
 * The returned date carries the **rental wall-clock in its local fields**, which is what the
 * rest of the app (datepicker, `toLocaleTimeString`, min/max comparisons) reads. That makes the
 * displayed time identical for every viewer, wherever they are. Use {@link toRentalInstant} to
 * turn it back into a real instant before sending it to a backend.
 */
export function createDate(): PrebookingDate
export function createDate(dateStr?: string, strategy?: CreationOptions): PrebookingDate
export function createDate(date?: Date, strategy?: CreationOptions): PrebookingDate
export function createDate(dateStr?: string | Date, strategy: CreationOptions = DEFAULT_OPTIONS): PrebookingDate {
  const result = new PrebookingDate(strategy.minutesRounding)
  if (dateStr) {
    const date = toZonedTime(new Date(dateStr), RENTAL_TIME_ZONE)
    result.date = date
    result.time = {
      hours: date.getHours(),
      minutes: date.getMinutes(),
    }
  }
  return result
}

/**
 * Rental wall-clock -> the instant it actually refers to.
 *
 * Mandatory at every boundary that hands a prebooking date to a backend: without it the
 * booked instant drifts by the viewer's UTC offset.
 */
export function toRentalInstant(wallClock: Date): Date {
  return fromZonedTime(wallClock, RENTAL_TIME_ZONE)
}

/**
 * Rental wall-clock -> a date whose **UTC** fields hold that calendar day.
 *
 * For the generated clients that serialise with `toISOString().substring(0, 10)`: passing the
 * wall-clock straight in would report the previous day for early-morning pick-ups.
 */
export function toRentalDay(wallClock: Date): Date {
  return new Date(Date.UTC(wallClock.getFullYear(), wallClock.getMonth(), wallClock.getDate()))
}

/**
 * Specific date class that follows the booking date constraints:
 *   - minutes can only be 0 or 30
 *   - date (year, month, day) and time (hour, minute, second) can be modified separately thanks to getter/setter methods
 */
export class PrebookingDate {
  private readonly minutesRoundingStrategy: RoundingStrategy

  constructor(minutesRoundingStrategy: RoundingStrategy) {
    this.minutesRoundingStrategy = minutesRoundingStrategy
  }

  private _date?: Date

  get date(): Date | undefined {
    if (!this._date) {
      return undefined
    }

    return new Date(this._date.getTime())
  }

  /**
   * Modify year, month, and/or day without modifying the hour, minute, or seconds
   * @param date
   */
  set date(date: Date) {
    if (date.getTime()) {
      this._date = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        this._date?.getHours() ?? 0,
        this._date?.getMinutes() ?? 0,
        0,
      )
    }
  }

  get dateTime(): Date | undefined {
    return this.date
  }

  /**
   * Modify everything (year, month, day, hours, minutes)
   * @param date
   */
  set dateTime(date: Date | undefined) {
    if (!date) {
      this._date = undefined
      return
    }

    const newDate = new Date(date)
    roundTime(newDate, this.minutesRoundingStrategy)
    this._date = newDate
  }

  get time(): Time | undefined {
    return this._date
      ? {
          hours: this._date.getHours(),
          minutes: this._date.getMinutes(),
        }
      : undefined
  }

  set time(time: Time) {
    if (this._date) {
      const newDate = new Date(this._date)
      // We cannot simply call the function on _date as normal reactivy want work
      // one will need to have deep watchers if the time is modified using this setter
      newDate.setHours(time.hours, time.minutes, 0, 0)
      roundTime(newDate, this.minutesRoundingStrategy)
      this._date = newDate
    }
  }

  toString(): string | undefined {
    return this._date?.toString()
  }

  // Not named `toJSON`: Pinia's `isPlainObject()` skips objects carrying one, so `$patch` rebinds
  // the state property instead of merging into it.
  toPersisted() {
    return { _date: this._date && toRentalInstant(this._date).toISOString() }
  }
}

/**
 * Round the minutes to 0 or 30 depnding on the strategy
 * @param date
 * @param roundingStrategy
 *   - later: we will round to the upper value (can increase the hour also). Examples:
 *      - 16:00 -> 16:00
 *      - 16:05 -> 16:30
 *      - 16:30 -> 16:30
 *      - 16:38 -> 17:00
 *   - sooner: we will round to the lower value (can increase the hour also). Examples:
 *      - 16:00 -> 16:00
 *      - 16:05 -> 16:00
 *      - 16:30 -> 16:30
 *      - 16:38 -> 16:30
 */
function roundTime(date: Date, roundingStrategy: RoundingStrategy) {
  const minutes = date.getMinutes()
  if (minutes === 0 || minutes === 30) {
    return
  }

  switch (roundingStrategy) {
    case 'later':
      if (minutes < 30) {
        date.setMinutes(30)
      }
      else {
        date.setMinutes(60)
      } // 60 so that it also increase the hour

      break
    case 'sooner':
      if (date.getMinutes() < 30) {
        date.setMinutes(0)
      }
      else {
        date.setMinutes(30)
      }

      break
    default:
      assertUnreachable(roundingStrategy)
  }
}

/**
 * Trick function to detect if the switch case is exhaustive
 *
 * @param arg
 */
function assertUnreachable(arg: never): never {
  throw new Error(`Did not expect to get here with ${arg}`)
}
